import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  XCircle,
  Eye,
  PackagePlus,
  History,
  ArrowDownToLine,
  ArrowUpFromLine,
  Wrench
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const emptyForm = {
  name: '',
  part_number: '',
  brand: '',
  category: '',
  quantity: 0,
  unit_cost: 0,
  min_stock: 0,
  machinery_id: '',
  purchased_by: '',
  purchase_date: '',
  invoice_number: '',
  supplier_company: '',
  supplier_contact: '',
  supplier_phone: '',
  status: 'active'
};

const emptyEntry = {
  quantity: 1,
  unit_cost: 0,
  date: new Date().toISOString().split('T')[0],
  supplier_company: '',
  supplier_contact: '',
  supplier_phone: '',
  invoice_number: '',
  purchased_by: '',
  notes: ''
};

const SpareParts = () => {
  const navigate = useNavigate();

  const [parts, setParts] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [filteredParts, setFilteredParts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [entryOpen, setEntryOpen] = useState(false);
  const [entryPart, setEntryPart] = useState(null);
  const [entryForm, setEntryForm] = useState(emptyEntry);
  const [savingEntry, setSavingEntry] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = parts.filter((p) => {
      if (p.status !== 'active') return false;
      if (!term) return true;

      return (
        String(p.name || '').toLowerCase().includes(term) ||
        String(p.part_number || '').toLowerCase().includes(term) ||
        String(p.category || '').toLowerCase().includes(term) ||
        String(p.brand || '').toLowerCase().includes(term) ||
        String(p.supplier_company || '').toLowerCase().includes(term)
      );
    });

    setFilteredParts(filtered);
  }, [searchTerm, parts]);

  const fetchData = async () => {
    try {
      const [partsRes, machineryRes] = await Promise.all([
        axios.get(`${API}/spare-parts`),
        axios.get(`${API}/machinery`)
      ]);

      setParts(partsRes.data);
      setMachinery(machineryRes.data);
    } catch (error) {
      toast.error('Error al cargar repuestos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyForm });
    setEditingPart(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPart) {
        await axios.put(`${API}/spare-parts/${editingPart.id}`, formData);
        toast.success('Ficha del repuesto actualizada');
      } else {
        await axios.post(`${API}/spare-parts`, formData);
        toast.success('Repuesto creado');
      }

      setDialogOpen(false);
      resetForm();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (part) => {
    setEditingPart(part);

    setFormData({
      name: part.name || '',
      part_number: part.part_number || '',
      brand: part.brand || '',
      category: part.category || '',
      quantity: part.quantity || 0,
      unit_cost: part.unit_cost || 0,
      min_stock: part.min_stock || 0,
      machinery_id: part.machinery_id || '',
      purchased_by: part.purchased_by || '',
      purchase_date: part.purchase_date || '',
      invoice_number: part.invoice_number || '',
      supplier_company: part.supplier_company || '',
      supplier_contact: part.supplier_contact || '',
      supplier_phone: part.supplier_phone || '',
      status: part.status || 'active'
    });

    setDialogOpen(true);
  };

  const openEntryDialog = (part) => {
    setEntryPart(part);

    setEntryForm({
      ...emptyEntry,
      unit_cost: Number(part.unit_cost || 0)
    });

    setEntryOpen(true);
  };

  const handleEntry = async (e) => {
    e.preventDefault();

    if (!entryPart) return;

    if (Number(entryForm.quantity) <= 0) {
      toast.error('La cantidad debe ser mayor a cero');
      return;
    }

    setSavingEntry(true);

    try {
      await axios.post(
        `${API}/spare-parts/${entryPart.id}/entries`,
        {
          ...entryForm,
          quantity: Number(entryForm.quantity),
          unit_cost: Number(entryForm.unit_cost)
        }
      );

      toast.success(`Entrada registrada: +${entryForm.quantity} unidades`);
      setEntryOpen(false);
      setEntryPart(null);
      setEntryForm({ ...emptyEntry });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar entrada');
    } finally {
      setSavingEntry(false);
    }
  };

  const openDetail = async (part) => {
    setSelectedPart(part);
    setDetailOpen(true);
    setLoadingMovements(true);

    try {
      const response = await axios.get(
        `${API}/spare-parts/${part.id}/movements`
      );
      setMovements(response.data);
    } catch (error) {
      setMovements([]);
      toast.error('Error al cargar el historial');
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleViewMaintenance = (maintenanceId) => {
    if (!maintenanceId) return;
    setDetailOpen(false);
    navigate(`/maintenance/${maintenanceId}`);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('¿Dar de baja este repuesto?')) return;

    try {
      await axios.patch(`${API}/spare-parts/${id}/deactivate`);
      toast.success('Repuesto dado de baja');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al dar de baja');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este repuesto permanentemente?')) return;

    try {
      await axios.delete(`${API}/spare-parts/${id}`);
      toast.success('Repuesto eliminado');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se puede eliminar este repuesto');
    }
  };

  const getMachineryName = (machineryId) => {
    const item = machinery.find((m) => String(m.id) === String(machineryId));
    return item?.name || '—';
  };

  const getMovementLabel = (type) => {
    const labels = {
      opening: 'Apertura',
      purchase: 'Compra',
      maintenance: 'Mantenimiento',
      return: 'Devolución',
      adjustment_in: 'Ajuste +',
      adjustment_out: 'Ajuste -'
    };

    return labels[type] || type;
  };

  const getMovementClass = (type) => {
    if (['opening', 'purchase', 'return', 'adjustment_in'].includes(type)) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }

    return 'bg-red-50 text-red-700 border-red-200';
  };

  const getSignedQuantity = (movement) => {
    if (['opening', 'purchase', 'return', 'adjustment_in'].includes(movement.movement_type)) {
      return `+${movement.quantity}`;
    }

    return `-${movement.quantity}`;
  };

  const totalEntries = movements
    .filter((m) => ['opening', 'purchase', 'return', 'adjustment_in'].includes(m.movement_type))
    .reduce((sum, m) => sum + Number(m.quantity || 0), 0);

  const totalExits = movements
    .filter((m) => ['maintenance', 'adjustment_out'].includes(m.movement_type))
    .reduce((sum, m) => sum + Number(m.quantity || 0), 0);

  const totalPurchased = movements
    .filter((m) => ['opening', 'purchase'].includes(m.movement_type))
    .reduce((sum, m) => sum + Number(m.total_cost || 0), 0);

  const activeCount = parts.filter((p) => p.status === 'active').length;
  const lowStockCount = parts.filter(
    (p) => p.status === 'active' && Number(p.quantity || 0) <= Number(p.min_stock || 0)
  ).length;
  const inventoryValue = parts
    .filter((p) => p.status === 'active')
    .reduce(
      (sum, p) => sum + Number(p.quantity || 0) * Number(p.unit_cost || 0),
      0
    );

  return (
    <div data-testid="spare-parts-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-800"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Repuestos
          </h1>
          <p className="text-slate-600 mt-1">
            Inventario, compras y kardex de repuestos
          </p>
        </div>

        <Button
          data-testid="add-part-button"
          onClick={openNewDialog}
          className="btn-primary w-full sm:w-auto"
        >
          <Plus size={20} className="mr-2" />
          Nuevo Repuesto
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-500">Repuestos activos</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{activeCount}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-500">Bajo stock</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{lowStockCount}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-500">Unidades</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">
            {parts.filter((p) => p.status === 'active').reduce((s, p) => s + Number(p.quantity || 0), 0)}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-500">Valor inventario</div>
          <div className="text-xl sm:text-2xl font-bold text-orange-600 mt-1">
            Bs. {inventoryValue.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={20}
          />
          <input
            type="text"
            data-testid="search-input"
            placeholder="Buscar por nombre, código, marca, categoría o proveedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando inventario...</div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5"
          data-testid="parts-grid"
        >
          {filteredParts.map((part) => (
            <div
              key={part.id}
              data-testid={`part-card-${part.id}`}
              className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-slate-800 leading-tight">
                    {part.name}
                  </h3>
                  <p className="text-xs text-slate-500 mono mt-1">
                    {part.part_number}
                  </p>
                </div>

                {part.quantity <= part.min_stock && (
                  <span className="shrink-0 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    Bajo stock
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">Stock actual</div>
                  <div className="text-2xl font-bold text-slate-800 mt-1">
                    {part.quantity}
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">Valor</div>
                  <div className="text-lg font-bold text-orange-600 mt-1">
                    Bs. {(Number(part.quantity || 0) * Number(part.unit_cost || 0)).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Marca</span>
                  <span className="font-medium text-right">{part.brand || '—'}</span>
                </div>

                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Categoría</span>
                  <span className="font-medium text-right">{part.category || '—'}</span>
                </div>

                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Costo unitario</span>
                  <span className="font-semibold">
                    Bs. {Number(part.unit_cost || 0).toFixed(2)}
                  </span>
                </div>

                {part.machinery_id && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Equipo</span>
                    <span className="font-medium text-orange-600 text-right">
                      {getMachineryName(part.machinery_id)}
                    </span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 mb-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openDetail(part)}
                  className="w-full"
                >
                  <Eye size={16} className="mr-1.5" />
                  Ver detalle
                </Button>

                <Button
                  type="button"
                  onClick={() => openEntryDialog(part)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <PackagePlus size={16} className="mr-1.5" />
                  Entrada
                </Button>
              </div>

              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <button
                  onClick={() => openEditDialog(part)}
                  className="btn-secondary flex items-center justify-center text-sm py-2"
                  data-testid={`edit-part-${part.id}`}
                >
                  <Edit size={16} className="mr-1" />
                  Editar
                </button>

                <button
                  onClick={() => handleDeactivate(part.id)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-3 rounded flex items-center justify-center text-sm transition-colors"
                  data-testid={`deactivate-part-${part.id}`}
                >
                  <XCircle size={16} className="mr-1" />
                  Baja
                </button>

                <button
                  onClick={() => handleDelete(part.id)}
                  className="p-2 hover:bg-red-50 rounded transition-colors"
                  data-testid={`delete-part-${part.id}`}
                  title="Eliminar"
                >
                  <Trash2 size={18} className="text-red-500" />
                </button>
              </div>
            </div>
          ))}

          {filteredParts.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              No se encontraron repuestos
            </div>
          )}
        </div>
      )}

      {/* NUEVO / EDITAR REPUESTO */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>
              {editingPart ? 'Editar ficha del repuesto' : 'Nuevo repuesto'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Número de Parte</label>
                <input
                  type="text"
                  value={formData.part_number}
                  onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                  className="input-field mono"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Marca</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Categoría</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {editingPart ? 'Stock actual' : 'Cantidad inicial'}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.quantity}
                  disabled={!!editingPart}
                  onChange={(e) => setFormData({
                    ...formData,
                    quantity: Number(e.target.value)
                  })}
                  className="input-field disabled:bg-slate-100 disabled:text-slate-500"
                />
                {editingPart && (
                  <p className="text-xs text-emerald-600 mt-1">
                    El stock se modifica mediante Entrada o mediante el consumo de mantenimiento.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Stock Mínimo</label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({
                    ...formData,
                    min_stock: Number(e.target.value)
                  })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Costo Unitario
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({
                    ...formData,
                    unit_cost: Number(e.target.value)
                  })}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Para Equipo
                </label>
                <select
                  value={formData.machinery_id}
                  onChange={(e) => setFormData({
                    ...formData,
                    machinery_id: e.target.value
                  })}
                  className="input-field"
                >
                  <option value="">Ninguno específico</option>
                  {machinery.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {!editingPart && (
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold text-slate-800 mb-3">
                  Compra inicial
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Comprado por
                    </label>
                    <input
                      type="text"
                      value={formData.purchased_by}
                      onChange={(e) => setFormData({
                        ...formData,
                        purchased_by: e.target.value
                      })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Fecha
                    </label>
                    <input
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({
                        ...formData,
                        purchase_date: e.target.value
                      })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Factura / Recibo
                    </label>
                    <input
                      type="text"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({
                        ...formData,
                        invoice_number: e.target.value
                      })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Proveedor
                    </label>
                    <input
                      type="text"
                      value={formData.supplier_company}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier_company: e.target.value
                      })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Contacto
                    </label>
                    <input
                      type="text"
                      value={formData.supplier_contact}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier_contact: e.target.value
                      })}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="text"
                      value={formData.supplier_phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier_phone: e.target.value
                      })}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>
            )}

            {editingPart && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                Las compras nuevas y sus datos de proveedor se registran desde
                <strong> Entrada</strong>, conservando todo el historial del kardex.
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
              <Button
                type="button"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancelar
              </Button>

              <Button type="submit" className="btn-primary">
                {editingPart ? 'Guardar cambios' : 'Crear repuesto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ENTRADA */}
      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>
              Registrar entrada de inventario
            </DialogTitle>
          </DialogHeader>

          {entryPart && (
            <form onSubmit={handleEntry} className="space-y-5">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Repuesto
                </div>
                <div className="font-bold text-slate-800 mt-1">
                  {entryPart.name}
                </div>
                <div className="text-xs text-slate-500 mono mt-1">
                  {entryPart.part_number}
                </div>
                <div className="mt-3 flex justify-between text-sm">
                  <span>Stock actual</span>
                  <strong>{entryPart.quantity}</strong>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Cantidad *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={entryForm.quantity}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      quantity: Number(e.target.value)
                    })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Costo unitario *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={entryForm.unit_cost}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      unit_cost: Number(e.target.value)
                    })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    value={entryForm.date}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      date: e.target.value
                    })}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Factura / Recibo
                  </label>
                  <input
                    type="text"
                    value={entryForm.invoice_number}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      invoice_number: e.target.value
                    })}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Proveedor
                  </label>
                  <input
                    type="text"
                    value={entryForm.supplier_company}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      supplier_company: e.target.value
                    })}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Comprado por
                  </label>
                  <input
                    type="text"
                    value={entryForm.purchased_by}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      purchased_by: e.target.value
                    })}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contacto proveedor
                  </label>
                  <input
                    type="text"
                    value={entryForm.supplier_contact}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      supplier_contact: e.target.value
                    })}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Teléfono proveedor
                  </label>
                  <input
                    type="text"
                    value={entryForm.supplier_phone}
                    onChange={(e) => setEntryForm({
                      ...entryForm,
                      supplier_phone: e.target.value
                    })}
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Observaciones
                </label>
                <textarea
                  value={entryForm.notes}
                  onChange={(e) => setEntryForm({
                    ...entryForm,
                    notes: e.target.value
                  })}
                  className="input-field min-h-[90px]"
                  placeholder="Observaciones de la compra..."
                />
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-800">Nueva existencia</span>
                  <strong className="text-emerald-700">
                    {Number(entryPart.quantity || 0) + Number(entryForm.quantity || 0)} unidades
                  </strong>
                </div>

                <div className="flex justify-between text-sm mt-1">
                  <span className="text-emerald-800">Total compra</span>
                  <strong className="text-emerald-700">
                    Bs. {(Number(entryForm.quantity || 0) * Number(entryForm.unit_cost || 0)).toFixed(2)}
                  </strong>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  onClick={() => setEntryOpen(false)}
                  className="btn-secondary"
                  disabled={savingEntry}
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={savingEntry}
                >
                  <PackagePlus size={17} className="mr-2" />
                  {savingEntry ? 'Registrando...' : 'Registrar entrada'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* DETALLE / KARDEX */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="w-[calc(100%-1rem)] sm:w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle>
              Detalle y kardex del repuesto
            </DialogTitle>
          </DialogHeader>

          {selectedPart && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                    {selectedPart.name}
                  </h2>
                  <div className="text-sm text-slate-500 mono mt-1">
                    {selectedPart.part_number}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                      {selectedPart.brand || 'Sin marca'}
                    </span>
                    <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                      {selectedPart.category || 'Sin categoría'}
                    </span>
                  </div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 min-w-[170px]">
                  <div className="text-xs text-orange-700">Stock actual</div>
                  <div className="text-3xl font-bold text-orange-700">
                    {selectedPart.quantity}
                  </div>
                  <div className="text-xs text-orange-700 mt-1">
                    Bs. {(Number(selectedPart.quantity || 0) * Number(selectedPart.unit_cost || 0)).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                  <div className="text-xs text-emerald-700">Entradas</div>
                  <div className="text-xl font-bold text-emerald-700">{totalEntries}</div>
                </div>

                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <div className="text-xs text-red-700">Salidas</div>
                  <div className="text-xl font-bold text-red-700">{totalExits}</div>
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <div className="text-xs text-blue-700">Costo actual</div>
                  <div className="text-lg font-bold text-blue-700">
                    Bs. {Number(selectedPart.unit_cost || 0).toFixed(2)}
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-xs text-slate-600">Compras históricas</div>
                  <div className="text-lg font-bold text-slate-800">
                    Bs. {totalPurchased.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <History size={18} className="text-slate-600" />
                  <h3 className="font-semibold text-slate-800">
                    Historial de movimientos
                  </h3>
                </div>

                {loadingMovements ? (
                  <div className="p-8 text-center text-slate-500">
                    Cargando kardex...
                  </div>
                ) : movements.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    No hay movimientos registrados.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {movements.map((movement) => {
                      const isEntry = ['opening', 'purchase', 'return', 'adjustment_in']
                        .includes(movement.movement_type);

                      return (
                        <div
                          key={movement.id}
                          className="p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                isEntry
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {isEntry ? (
                                <ArrowDownToLine size={19} />
                              ) : (
                                <ArrowUpFromLine size={19} />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={`px-2 py-1 rounded-md border text-xs font-semibold ${getMovementClass(movement.movement_type)}`}
                                >
                                  {getMovementLabel(movement.movement_type)}
                                </span>

                                <span className="text-sm text-slate-500">
                                  {movement.date}
                                </span>
                              </div>

                              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                                <div>
                                  <span className="text-slate-500">Cantidad: </span>
                                  <strong>{getSignedQuantity(movement)}</strong>
                                </div>

                                <div>
                                  <span className="text-slate-500">Costo: </span>
                                  <strong>
                                    Bs. {Number(movement.unit_cost || 0).toFixed(2)}
                                  </strong>
                                </div>

                                <div>
                                  <span className="text-slate-500">Total: </span>
                                  <strong>
                                    Bs. {Number(movement.total_cost || 0).toFixed(2)}
                                  </strong>
                                </div>

                                <div>
                                  <span className="text-slate-500">Factura: </span>
                                  <strong>{movement.invoice_number || '—'}</strong>
                                </div>
                              </div>

                              {(movement.supplier_company ||
                                movement.reference_id ||
                                movement.notes ||
                                movement.maintenance) && (
                                <div className="mt-2 text-xs text-slate-500 space-y-2">
                                  {movement.supplier_company && (
                                    <div>
                                      Proveedor: <strong>{movement.supplier_company}</strong>
                                    </div>
                                  )}

                                  {movement.maintenance ? (
                                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                        <div className="min-w-0 space-y-1">
                                          <div className="font-semibold text-blue-800">
                                            Mantenimiento
                                          </div>

                                          <div className="text-sm font-semibold text-slate-800">
                                            {movement.maintenance.description || 'Sin descripción'}
                                          </div>

                                          {movement.maintenance.machinery_name && (
                                            <div>
                                              <span className="text-slate-500">Maquinaria: </span>
                                              <strong>
                                                {movement.maintenance.machinery_name}
                                              </strong>
                                              {movement.maintenance.machinery_internal_code && (
                                                <span className="text-slate-500">
                                                  {' '}({movement.maintenance.machinery_internal_code})
                                                </span>
                                              )}
                                            </div>
                                          )}

                                          {movement.maintenance.type && (
                                            <div>
                                              <span className="text-slate-500">Tipo: </span>
                                              <strong>{movement.maintenance.type}</strong>
                                            </div>
                                          )}

                                          {movement.maintenance.main_technician && (
                                            <div>
                                              <span className="text-slate-500">Técnico: </span>
                                              <strong>
                                                {movement.maintenance.main_technician}
                                              </strong>
                                            </div>
                                          )}
                                        </div>

                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100"
                                          onClick={() =>
                                            handleViewMaintenance(movement.maintenance.id)
                                          }
                                        >
                                          <Wrench size={14} className="mr-1.5" />
                                          Ver mantenimiento
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    movement.reference_id && (
                                      <div>
                                        Referencia:{' '}
                                        <span className="mono">{movement.reference_id}</span>
                                      </div>
                                    )
                                  )}

                                  {movement.notes && (
                                    <div>{movement.notes}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  onClick={() => {
                    setDetailOpen(false);
                    openEntryDialog(selectedPart);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <PackagePlus size={17} className="mr-2" />
                  Registrar nueva entrada
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpareParts;
